/**
 * Version Tracking Utility
 * Helps track and manage document versions for optimistic locking
 */

export interface VersionedDocument {
  id?: string;
  __system?: {
    version?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Extract version number from a document
 */
export function getVersion(doc: VersionedDocument | null | undefined): number {
  if (!doc) return 0;
  return doc.__system?.version || 0;
}

/**
 * Create a versioned update payload
 */
export function createVersionedUpdate<T extends VersionedDocument>(
  doc: T,
  updates: Partial<T>
): Partial<T> & { __system?: { version: number } } {
  const currentVersion = getVersion(doc);
  return {
    ...updates,
    __system: {
      ...doc.__system,
      version: currentVersion,
    },
  };
}

/**
 * Check if a document has a version
 */
export function hasVersion(doc: VersionedDocument | null | undefined): boolean {
  return getVersion(doc) > 0;
}

/**
 * Create a versioned document reference (for storing expected version)
 */
export interface VersionedDocumentRef {
  id: string;
  version: number;
  entityType: string; // 'product', 'invoice', 'customer', etc.
}

/**
 * Store version reference in session storage (for conflict detection)
 */
export function storeVersionRef(ref: VersionedDocumentRef): void {
  const key = `version_ref_${ref.entityType}_${ref.id}`;
  sessionStorage.setItem(key, JSON.stringify(ref));
}

/**
 * Get stored version reference
 */
export function getVersionRef(entityType: string, id: string): VersionedDocumentRef | null {
  const key = `version_ref_${entityType}_${id}`;
  const stored = sessionStorage.getItem(key);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Clear all stored version references (session storage).
 */
export function clearAllVersionRefs(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('version_ref_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Clear version reference
 */
export function clearVersionRef(entityType: string, id: string): void {
  const key = `version_ref_${entityType}_${id}`;
  sessionStorage.removeItem(key);
}

